import { Component, OnInit, OnDestroy, DestroyRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { Editor } from 'primeng/editor';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { OnboardingService } from '../../services/onboarding.service';
import { EmailTemplate, CreateEmailTemplate } from '../../models/onboarding.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

@Component({
  selector: 'app-email-template-management',
  standalone: true,
  imports: [
    CommonModule, FormsModule, Button, Select, InputText, Checkbox,
    Editor, Tooltip, Tag, Dialog, ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './email-template-management.component.html',
  styleUrl: './email-template-management.component.scss'
})
export class EmailTemplateManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);

  @ViewChild('bodyEditor') bodyEditor!: Editor;

  templates: EmailTemplate[] = [];
  filteredTemplates: EmailTemplate[] = [];
  searchText = '';
  loading = true;
  selectedTemplate: EmailTemplate | null = null;
  isEditMode = false;
  fieldTouched: Record<string, boolean> = {};
  showPreview = false;

  templateTypeOptions = [
    { label: 'Welcome', value: 'Welcome' },
    { label: 'Onboarding', value: 'Onboarding' },
    { label: 'All-Hearts', value: 'AllHearts' }
  ];

  receiverOptions = [
    { label: 'Personal Email', value: 'personal' },
    { label: 'Work Email', value: 'work' },
    { label: 'Both', value: 'both' }
  ];

  placeholders = [
    '{{EmployeeName}}', '{{FirstName}}', '{{Department}}',
    '{{Designation}}', '{{JoiningDate}}', '{{ManagerName}}',
    '{{HeadOfDepartmentName}}', '{{CompanyName}}', '{{PersonalEmail}}'
  ];

  constructor(
    private onboardingService: OnboardingService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(false);
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.pageHeaderService.setCanShowExport(false);
    this.pageHeaderService.setPlaceholder('Search templates...');

    this.searchText = this.pageHeaderService.currentSearchText;
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => { this.searchText = v ?? ''; this.applySearch(); });

    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  applySearch(): void {
    const s = this.searchText.trim().toLowerCase();
    this.filteredTemplates = s
      ? this.templates.filter(t => t.name.toLowerCase().includes(s) || t.templateType.toLowerCase().includes(s))
      : [...this.templates];
  }

  loadTemplates(): void {
    this.loading = true;
    this.onboardingService.getAllTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.templates = data || [];
          this.applySearch();
          this.loading = false;
          if (this.templates.length > 0 && !this.selectedTemplate) {
            this.selectTemplate(this.templates[0]);
          }
        },
        error: (err) => {
          console.error('Failed to load templates:', err);
          this.templates = [];
          this.loading = false;
        }
      });
  }

  selectTemplate(template: EmailTemplate): void {
    this.selectedTemplate = { ...template };
    this.isEditMode = true;
    this.fieldTouched = {};
    this.showPreview = false;
  }

  addNewTemplate(): void {
    this.selectedTemplate = {
      id: '', name: '', subject: '', body: '', templateType: 'Welcome',
      placeholderVariables: '', cc: '', receiver: 'personal', isActive: true
    };
    this.isEditMode = false;
    this.fieldTouched = {};
    this.showPreview = false;
  }

  saveTemplate(): void {
    if (!this.selectedTemplate) return;
    this.fieldTouched['name'] = true;
    this.fieldTouched['subject'] = true;
    if (!this.selectedTemplate.name.trim() || !this.selectedTemplate.subject.trim()) return;

    if (this.isEditMode) {
      const savedId = this.selectedTemplate.id;
      this.onboardingService.updateTemplate(savedId, this.selectedTemplate)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            this.messageService.add({ severity: 'success', summary: 'Saved' });
            const idx = this.templates.findIndex(t => t.id === savedId);
            if (idx >= 0) this.templates[idx] = updated;
            this.applySearch();
            this.selectedTemplate = { ...updated };
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save' })
        });
    } else {
      const dto: CreateEmailTemplate = {
        name: this.selectedTemplate.name,
        subject: this.selectedTemplate.subject,
        body: this.selectedTemplate.body,
        templateType: this.selectedTemplate.templateType,
        placeholderVariables: this.selectedTemplate.placeholderVariables,
        cc: this.selectedTemplate.cc,
        receiver: this.selectedTemplate.receiver,
        isActive: this.selectedTemplate.isActive
      };
      this.onboardingService.createTemplate(dto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (created) => {
            this.messageService.add({ severity: 'success', summary: 'Created' });
            this.templates.push(created);
            this.applySearch();
            this.selectTemplate(created);
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create' })
        });
    }
  }

  deleteTemplate(): void {
    if (!this.selectedTemplate || !this.isEditMode) return;
    this.confirmationService.confirm({
      message: `Delete "${this.selectedTemplate.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.onboardingService.deleteTemplate(this.selectedTemplate!.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.messageService.add({ severity: 'success', summary: 'Deleted' });
              this.templates = this.templates.filter(t => t.id !== this.selectedTemplate!.id);
              this.applySearch();
              if (this.templates.length > 0) {
                this.selectTemplate(this.templates[0]);
              } else {
                this.selectedTemplate = null;
                this.isEditMode = false;
              }
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete' })
          });
      }
    });
  }

  seedDefaults(): void {
    this.onboardingService.seedDefaultEmailTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Seeded', detail: 'Default templates created' });
          this.selectedTemplate = null;
          this.loadTemplates();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to seed' })
      });
  }

  insertPlaceholder(ph: string): void {
    if (!this.selectedTemplate) { return; }

    // Insert at cursor position in Quill editor
    const quill = this.bodyEditor?.getQuill?.();
    if (quill) {
      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength() - 1;
      quill.insertText(index, ph, 'user');
      quill.setSelection(index + ph.length, 0);
    } else {
      // Fallback: append to body
      this.selectedTemplate.body = (this.selectedTemplate.body || '') + ph;
    }
  }

  getPreviewHtml(): SafeHtml {
    if (!this.selectedTemplate) { return ''; }
    const html = (this.selectedTemplate.body || '')
      .replace(/\{\{EmployeeName\}\}/g, 'John Diaz')
      .replace(/\{\{FirstName\}\}/g, 'John')
      .replace(/\{\{Department\}\}/g, 'Software Development')
      .replace(/\{\{Designation\}\}/g, 'Software Engineer')
      .replace(/\{\{JoiningDate\}\}/g, '2026-04-10')
      .replace(/\{\{ManagerName\}\}/g, 'Lakshan Fernando')
      .replace(/\{\{HeadOfDepartmentName\}\}/g, 'Lakshan Fernando')
      .replace(/\{\{CompanyName\}\}/g, 'BISTEC Global')
      .replace(/\{\{PersonalEmail\}\}/g, 'john@email.com');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getPreviewSubject(): string {
    if (!this.selectedTemplate) return '';
    return (this.selectedTemplate.subject || '')
      .replace(/\{\{EmployeeName\}\}/g, 'John Diaz')
      .replace(/\{\{CompanyName\}\}/g, 'BISTEC Global');
  }
}
